import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { GoalService } from "@gen/tracker/v1/goal_connect";
import { ConversionGoal, type GoalPresetDefinition } from "@gen/tracker/v1/goal_pb";
import { reportingSchemaKeys } from "./reporting-schema-service";

const client = createClient(GoalService);

export const goalKeys = {
  all: ["goals"] as const,
  list: (includeArchived: boolean) => [...goalKeys.all, "list", includeArchived] as const,
  presets: () => [...goalKeys.all, "presets"] as const,
};

/** Goal categories accepted by the registry (server-validated). */
export const GOAL_CATEGORIES = [
  "registration",
  "deposit",
  "rebill",
  "sale",
  "lead",
  "upsell",
  "install",
  "trial",
  "custom",
] as const;

export function useListGoals(includeArchived = false) {
  return useQuery({
    queryKey: goalKeys.list(includeArchived),
    queryFn: async (): Promise<ConversionGoal[]> => {
      const res = await client.listGoals({ includeArchived });
      return res.items;
    },
  });
}

/**
 * Goal mutations also invalidate the reporting schema: goals generate
 * per-goal metric definitions (goal.<key>.count/hold/rejected/revenue/avg).
 */
function useInvalidateGoals() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: goalKeys.all });
    void qc.invalidateQueries({ queryKey: reportingSchemaKeys.all });
  };
}

export function useCreateGoal() {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (goal: ConversionGoal) => {
      const res = await client.createGoal({ goal });
      return res.goal;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateGoal() {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (goal: ConversionGoal) => {
      const res = await client.updateGoal({ goal });
      return res.goal;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteGoal() {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteGoal({ id });
      return id;
    },
    onSuccess: invalidate,
  });
}

export function useListPresets() {
  return useQuery({
    queryKey: goalKeys.presets(),
    staleTime: 30 * 60_000,
    queryFn: async (): Promise<GoalPresetDefinition[]> => {
      const res = await client.listPresets({});
      return res.presets;
    },
  });
}

export function useSeedPresetGoals() {
  const invalidate = useInvalidateGoals();
  return useMutation({
    mutationFn: async (preset: string) => {
      const res = await client.seedPresetGoals({ preset });
      return res.items;
    },
    onSuccess: invalidate,
  });
}
